import { FEE_LABEL } from '@serievo/contracts';
import { Alert, Card, Col, Row, Skeleton, Table, Typography } from 'antd';
import { formatMoney } from '../api/client';
import { useCashflow } from '../api/reports';
import { HelpTip, Kpi, Money, periodLabel, PlatformTag, viDate } from '../components/ui';
import { useWork } from '../features/context/WorkContext';

/** Dòng tiền: GMV, phí sàn, hoàn trả, tiền về và tiền sàn còn giữ. */
export function CashflowPage() {
  const w = useWork();
  const q = useCashflow(w);
  const d = q.data;
  if (q.error) return <Alert style={{ margin: 16 }} type="error" showIcon message={q.error.message} />;
  if (!d) return <Skeleton active style={{ padding: 16 }} />;
  const shopName = (platform: string, shopId: string) =>
    w.entity.shops.find((s) => s.platform === platform && s.shopId === shopId)?.shopName ?? shopId;

  return (
    <div className="page">
      <Typography.Title level={4} className="page-title">
        Dòng tiền · {periodLabel(d.period)}
      </Typography.Title>
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} xl={8} xxl={4}>
          <Kpi k="revenue" title="GMV (tiền hàng quyết toán)" value={formatMoney(d.gmv)} suffix="₫" />
        </Col>
        <Col xs={24} sm={12} xl={8} xxl={4}>
          <Kpi k="fee" title="Phí sàn" value={formatMoney(d.feesTotal)} suffix="₫" />
        </Col>
        <Col xs={24} sm={12} xl={8} xxl={4}>
          <Kpi k="time" title="Hoàn tiền trả hàng" value={formatMoney(d.refunds)} suffix="₫" />
        </Col>
        <Col xs={24} sm={12} xl={8} xxl={4}>
          <Kpi k="brand" title="Ròng sau phí" value={formatMoney(d.netSettlement)} suffix="₫" />
        </Col>
        <Col xs={24} sm={12} xl={8} xxl={4}>
          <Kpi k="matched" title="Tiền về trong kỳ" value={formatMoney(d.payoutsInPeriod)} suffix="₫" />
        </Col>
        <Col xs={24} sm={12} xl={8} xxl={4}>
          <Kpi
            k="tax"
            title="Sàn còn giữ cuối kỳ"
            value={formatMoney(d.heldAtEnd)}
            suffix="₫"
            hint={<HelpTip text="Tổng ròng đã quyết toán trừ tổng đã chuyển về tài khoản, lũy kế đến hết kỳ." />}
          />
        </Col>
      </Row>
      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={24} lg={10}>
          <Card size="small" title="Phí sàn theo loại">
            <Table
              size="small"
              rowKey="feeCode"
              pagination={false}
              dataSource={d.fees}
              columns={[
                { title: 'Khoản phí', dataIndex: 'feeCode', render: (c: string) => FEE_LABEL[c] ?? c },
                { title: 'Số tiền', dataIndex: 'amount', align: 'right', render: (v: string) => <Money v={v} /> },
              ]}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <b>Tổng phí</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Money v={d.feesTotal} strong />
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card size="small" title="Theo shop">
            <Table
              size="small"
              rowKey={(r) => `${r.platform}:${r.shopId}`}
              pagination={false}
              dataSource={d.byShop}
              scroll={{ x: 560 }}
              columns={[
                {
                  title: 'Shop',
                  key: 'shop',
                  render: (_, r) => (
                    <>
                      <PlatformTag p={r.platform} /> {shopName(r.platform, r.shopId)}
                    </>
                  ),
                },
                { title: 'GMV', dataIndex: 'gmv', align: 'right', render: (v: string) => <Money v={v} /> },
                { title: 'Phí', dataIndex: 'fees', align: 'right', render: (v: string) => <Money v={v} /> },
                { title: 'Ròng', dataIndex: 'net', align: 'right', render: (v: string) => <Money v={v} /> },
                { title: 'Đã về', dataIndex: 'paid', align: 'right', render: (v: string) => <Money v={v} strong /> },
              ]}
            />
          </Card>
        </Col>
      </Row>
      <Card size="small" title="Các lần sàn chuyển tiền về" style={{ marginTop: 12 }}>
        <Table
          size="small"
          rowKey="payoutRef"
          dataSource={d.payouts}
          pagination={{ pageSize: 15, showSizeChanger: false }}
          scroll={{ x: 640 }}
          columns={[
            { title: 'Ngày về', dataIndex: 'payoutDate', width: 110, render: viDate },
            { title: 'Shop', key: 'shop', render: (_, r) => <><PlatformTag p={r.platform} /> {shopName(r.platform, r.shopId)}</> },
            { title: 'Mã giao dịch', dataIndex: 'payoutRef', className: 'num' },
            { title: 'Tài khoản', dataIndex: 'bankAccountMasked' },
            { title: 'Số tiền', dataIndex: 'amount', align: 'right', render: (v: string) => <Money v={v} strong /> },
          ]}
        />
      </Card>
    </div>
  );
}
